// import pdpQuery from '~layers/app/graphql/pdp.gql'

export function usePDP(meta: { value?: { sku?: string, parents: any[] } }) {
  const { public: config } = useRuntimeConfig()
  const productData = useState('productData', () => null)
  const productError = useState<unknown>('productError', () => null)
  const stockData = useState('stockData', () => ({}))
  const pendingStock = useState('pendingStock', () => false)
  const locale = config.strapiLocale
  const status = 'PUBLISHED'

  const variables = {
    sku: meta.value?.sku,
    locale,
    status,
    plpUrls: meta.value?.parents?.map((parent: any) => parent.url) || [],
  }

  async function fetchProductData() {
    // const { data, error } = await useAsyncQuery<any>(pdpQuery, variables)
    const result = await useGraphqlQuery('pdp', variables, useGraphqlDefaultClient())

    if (!result.data) {
      return
    }

    productData.value = result.data
    productError.value = result.errors
  }

  const esProduct = computed(() => {
    return productData?.value?.esProduct
  })

  const m2Product = computed(() => {
    const product = {
      ...(productData?.value?.products?.items?.[0] || {}),
    }
    delete product.categories

    if (esProduct.value) {
      product.product = esProduct.value
    }

    if (stockData.value?.products?.items?.[0]?.variants?.length) {
      product.variants = product.variants?.map((variant, index) => ({ ...variant, product: { ...variant.product, ...stockData.value?.products?.items?.[0]?.variants[index].product } }))
    }
    if (stockData.value?.products?.items?.[0]?.stock_status) {
      product.stockItem = stockData.value?.products?.items?.[0].stockItem
      product.stock_left = stockData.value?.products?.items?.[0].stock_left
      product.stock_status = stockData.value?.products?.items?.[0].stock_status
    }
    if (stockData.value?.products?.items?.[0]?.items) {
      product.items = result.items.map((item, index) => {
        if (stockData.value?.products?.items?.[0]?.items[index]?.product?.variants?.length) {
          return {
            ...item,
            product: {
              ...item.product,
              ...stockData.value?.products?.items?.[0]?.items[index]?.product,
              variants: item.product.variants.map((variant, vIndex) => ({
                ...variant,
                product: { ...variant.product, ...stockData.value?.products?.items?.[0]?.items[index]?.product.variants[vIndex].product },
              })),
            },
          }
        }
        if (stockData.value?.products?.items?.[0]?.items[index]?.product?.stock_status) {
          return { ...item, product: { ...item.product, ...stockData.value.products.items[0].items[index].product } }
        }
        return { ...item }
      })
    }
    return product
  })

  const crosssellProducts = computed(() =>
    m2Product.value?.crosssell_products?.map(item => item.product) || [],
  )

  const upsellProducts = computed(() =>
    m2Product.value?.upsell_products?.map(item => item.product) || [],
  )

  const relatedProducts = computed(() =>
    m2Product.value?.related_products?.map(item => item.product) || [],
  )

  const interestingProducts = computed(() =>
    m2Product.value?.interesting_products?.map(item => item.product) || [],
  )

  const attributes = computed<Record<string, string>>(() => {
    return m2Product.value?.attributes?.items?.reduce((acc: Record<string, any>, attribute: { code: string, value?: string, selected_options?: any }) => {
      if (attribute.value) {
        acc[attribute.code] = attribute.value
      }
      else if (attribute.selected_options) {
        acc[attribute.code] = attribute.selected_options.map((option: { label: string }) => option.label).join(', ')
      }
      return acc
    }, {}) || {}
  })

  const stockDetails = computed<any>(() => JSON.parse(attributes.value.poo_stock_details || '{}'))

  const categories = computed(() => {
    return [
      ...(productData?.value?.products?.items?.[0]?.categories || []),
    ].filter(item => item.url_path).sort((a: { level: number }, b: { level: number }) => b.level - a.level).map((category: { url_path: string, name: string, level: number }) => {
      return {
        ...category,
        url: category.url_path,
        label: category.name,
        level: category.level,
      }
    })
  })

  const parentCategories = computed(() => {
    let parent: { url?: string } = {}
    const parents: Array<{ url?: string }> = []

    categories.value.forEach((category) => {
      if (category.url && (!parent.url || parent.url.startsWith(category.url))) {
        parent = category
        parents.push(category)
      }
    })

    return parents
  })

  const pdpUsps = computed(() => {
    return productData?.value?.pdpUsps?.[0]?.Usp || []
  })

  const plpData = computed(() => {
    return productData?.value?.plpData || []
  })

  const contentBlocks = computed(() => {
    return productData?.value?.contentBlocks?.[0] || null
  })

  const plpUsps = computed(() => {
    return plpData.value?.nodes?.filter((node: any) => node?.PLPData?.Usp?.Usp?.length > 0)?.[0]?.PLPData?.Usp?.Usp || []
  })

  const loadStockData = () => {
    pendingStock.value = true
    useGraphqlQuery('productStock', { sku: meta.value?.sku }, useGraphqlCustomerClient()).then((response) => {
      stockData.value = response.data
    }).finally(() => {
      pendingStock.value = false
    })
  }

  return {
    data: productData,
    attributes,
    esProduct,
    m2Product,
    pdpUsps,
    plpUsps,
    plpData,
    contentBlocks,
    categories,
    parentCategories,
    error: productError,
    crosssellProducts,
    upsellProducts,
    relatedProducts,
    interestingProducts,
    fetchProductData,
    stockData,
    loadStockData,
    pendingStock,
    stockDetails,
  }
}
